import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { BROTO_URL } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/");
      } else {
        if (password.trim().length < 12 || new TextEncoder().encode(password).length > 72) {
          setError("Use uma senha com pelo menos 12 caracteres. Senhas com muitos acentos ou emojis podem atingir o limite de 72 bytes.");
          return;
        }
        const result = await register(name, email, password);
        setPassword("");
        setNotice(result.message);
        setMode("login");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src="https://images.unsplash.com/photo-1619099619226-f96e319e3732?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Set de produção" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/40 to-transparent" />
        <img src={BROTO_URL} alt="Broto Netzer" className="absolute right-8 bottom-40 w-48 xl:w-64 object-contain opacity-95 pointer-events-none" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="caption-label mb-3">24FPS / 4K RAW · 2.39:1 CINEMATIC</p>
          <h1 className="font-serif text-5xl text-[#F4F4F0] leading-tight">
            Estratégia de conteúdo<br /><span className="gold-italic">& produção audiovisual.</span>
          </h1>
        </div>
        <div className="absolute top-8 left-8 caption-label">[+] REC</div>
        <div className="absolute top-8 right-8 caption-label">A/B [+]</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12 film-grain">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 25, stiffness: 200 }} className="w-full max-w-md">
          <div className="mb-10">
            <div className="mb-8 flex items-center gap-4">
              <img src="/assets/netzer-logo-round.png" alt="Netzer Studio" className="h-16 w-16 rounded-full object-cover" data-testid="login-logo" />
              <div className="leading-none">
                <p className="font-serif text-2xl text-[#F4F4F0] uppercase">Netzer</p>
                <p className="font-mono text-[10px] text-[#71717A] tracking-[0.35em] uppercase mt-1">Studio // OS</p>
              </div>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F4F4F0] tracking-tight">Acesso ao <span className="gold-italic">Estúdio</span></h2>
            <p className="text-sm text-[#A1A1AA] mt-2">Gestão completa da sua produtora — comercial, captações, agenda e financeiro.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="register-name" className="caption-label">Nome</Label>
                <Input id="register-name" maxLength={120} autoComplete="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 bg-[#141418] border-[#27272A] text-[#F4F4F0] focus-visible:ring-[#8CC63F]" placeholder="Seu nome" />
              </div>
            )}
            <div>
              <Label htmlFor="login-email" className="caption-label">E-mail</Label>
              <Input id="login-email" autoComplete="username" maxLength={254} data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-[#141418] border-[#27272A] text-[#F4F4F0] focus-visible:ring-[#8CC63F]" placeholder="voce@estudio.com.br" />
            </div>
            <div>
              <Label htmlFor="login-password" className="caption-label">Senha</Label>
              <Input id="login-password" data-testid="login-password-input" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={mode === "register" ? 12 : undefined} value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-[#141418] border-[#27272A] text-[#F4F4F0] focus-visible:ring-[#8CC63F]" placeholder="••••••••" />
              {mode === "register" && <p className="mt-2 text-sm text-[#A1A1AA]">Use pelo menos 12 caracteres. O administrador precisará aprovar seu acesso.</p>}
            </div>
            {notice && <p role="status" data-testid="register-pending-notice" className="text-sm text-[#8CC63F] border border-[#8CC63F]/25 rounded-lg px-3 py-2">{notice}</p>}
            {error && <p role="alert" data-testid="login-error" className="text-sm text-[#F43F5E] bg-[#F43F5E]/10 border border-[#F43F5E]/25 rounded-lg px-3 py-2">{error}</p>}
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] font-semibold rounded-full h-11 transition-colors">
              {loading ? "Aguarde…" : mode === "login" ? "Entrar no estúdio" : "Solicitar acesso"}
            </Button>
          </form>

          <button disabled={loading} data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNotice(""); }} className="mt-6 text-sm text-[#71717A] hover:text-[#8CC63F] transition-colors">
            {mode === "login" ? "Ainda não tem acesso? Solicitar cadastro" : "Já tem conta? Entrar"}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
